import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { CourierBadge, AudioFX, ConfirmDialog } from '../ui/primitives';
import { courierOfTitle } from '../ui/couriers';
import { usePackingStation } from '../contexts/PackingStationContext';
import { useKiosk } from './StationApp';
import { API_URL } from '../config/api';

/* Kolejka pakowania z UCS (GET /api/packing/orders → proxy /v1/packages/now):
   płaskie, carrier-agnostic "paczki do spakowania" pogrupowane po category. */

export type UcsPackOrder = {
  shopifyOrderId: number;
  orderName: string;
  createdAt: string;
  fulfillmentStatus: string;
  deliveryDate?: string | null;
  customer: {
    name?: string | null;
    city?: string | null;
    postalCode?: string | null;
    addressLine1?: string | null;
  };
  items: { title: string; quantity: number; sku?: string | null }[];
  carrier?: { pluginId?: string; serviceId?: string; title?: string | null } | null;
  category: { id: string | null; name: string; cutoffTime?: string | null };
  // Priorytet klienta (ADR-0007); kolejka z UCS jest już po nim posortowana.
  priority?: { score: number; vip: boolean; mode: 'basic' | 'advanced' };
  reason: 'identified' | 'no_shipping_line' | 'unknown_service_code';
  packingStatus: 'packed' | 'packing' | 'not_started';
  // Etykiety produktowe tego zamówienia zostały wydrukowane (drukarka skończyła) od ostatniej
  // zakończonej sesji — liczone w API z kartoteki wydruków (`api/src/modules/packing/labelsPrinted.ts`).
  labelsPrinted?: boolean;
  // Magazyn (moduł opcjonalny): czy produkty zamówienia są na stanie. null = moduł wyłączony,
  // znaczniki ukryte albo stan dnia jeszcze nieznany.
  stock?: PackStockTag | null;
};

export type PackStockTag = {
  status: 'ok' | 'partial' | 'short';
  label: string;
  missing: Array<{ key?: string; name: string; quantity: number }>;
  units?: number;
};

type QueueResponse = {
  packages: UcsPackOrder[];
  categories: { id: string | null; name: string; count: number; cutoffTime?: string | null }[];
  // Ustawienia → Panel pakowania: nierozpoznane zamówienia na górze listy (kategorie bez zmian).
  unidentifiedFirst?: boolean;
  // Magazyn: stan dnia znany? pytać przed pakowaniem zamówienia bez pełnego stanu?
  stock?: { stockKnown: boolean; warnOnStart: boolean } | null;
};

const STOCK_TONE: Record<PackStockTag['status'], { icon: string; bg: string; fg: string }> = {
  ok: { icon: 'circle-check', bg: 'var(--success-soft)', fg: 'var(--success-text)' },
  partial: { icon: 'circle-alert', bg: 'var(--warning-soft)', fg: 'var(--warning-text)' },
  short: { icon: 'circle-x', bg: 'var(--danger-soft)', fg: 'var(--danger-text)' },
};

/** „Żytni ×1, Grahamka ×2" — krótko, do jednej linii kafelka i okna ostrzeżenia. */
function missingText(tag: PackStockTag): string {
  return tag.missing.map((m) => `${m.name} ×${m.quantity}`).join(', ');
}

export default function OrdersScreen() {
  const navigate = useNavigate();
  const { user, station } = usePackingStation();
  const { push, compact, setSession } = useKiosk();
  const [filter, setFilter] = useState<string>('all');
  const [q, setQ] = useState('');
  const [starting, setStarting] = useState<number | null>(null);
  // Zamówienie bez pełnego stanu czeka na potwierdzenie operatora (Magazyn → „Pytaj przed pakowaniem").
  const [stockAsk, setStockAsk] = useState<UcsPackOrder | null>(null);

  const { data, isLoading } = useQuery<QueueResponse>({
    queryKey: ['pack-orders'],
    queryFn: async () => (await axios.get(`${API_URL}/api/packing/orders`)).data,
    refetchInterval: 15000,
  });
  // Kolejność = DOKŁADNIE to, co zwraca UCS (ADR-0007): wstępnie posortowane po
  // priorytecie klienta (VIP → punkty → wiek zamówienia). NIE re-sortujemy lokalnie po
  // numerze — to nadpisywało priorytet. W trybie basic UCS i tak zwraca najstarsze pierwsze.
  // NIE chowamy zamówień po lokalnym `packingStatus`: zamówienie znika z kolejki dopiero, gdy
  // fulfillment w Shopify przejdzie (UCS przestaje je wtedy zwracać).
  const orders = useMemo(() => [...(data?.packages || [])], [data]);
  const categories = data?.categories || [];

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: orders.length };
    orders.forEach((o) => {
      const k = o.category?.id ?? 'others';
      c[k] = (c[k] || 0) + 1;
    });
    return c;
  }, [orders]);

  // Indeks kategorii = kolejność z UCS `categories[]` (już ułożone: cutoff najwcześniejszy
  // pierwszy → DHL przed InPost → „Inne" na końcu). Używamy go do GRUPOWANIA listy w zakładce
  // „Wszystkie": najpierw cała grupa z najbliższym cutoffem (np. wszystkie DHL-e), a wewnątrz
  // grupy najbardziej priorytetowy klient pierwszy (sort STABILNY zachowuje porządek z UCS,
  // który jest już po priorytecie). W widoku jednej kategorii to no-op.
  const catOrder = useMemo(() => {
    const m = new Map<string, number>();
    (data?.categories || []).forEach((c, i) => m.set(c.id ?? 'others', i));
    return m;
  }, [data]);
  const catRank = (o: UcsPackOrder): number =>
    catOrder.get(o.category?.id ?? 'others') ?? Number.MAX_SAFE_INTEGER;

  const filtered = orders
    .filter((o) => {
      const okF = filter === 'all' || (o.category?.id ?? 'others') === filter;
      const okQ =
        !q ||
        o.orderName.toLowerCase().includes(q.toLowerCase()) ||
        (o.customer?.name || '').toLowerCase().includes(q.toLowerCase());
      return okF && okQ;
    })
    // Z „Nierozpoznane na górze" (Ustawienia → Panel pakowania) nierozpoznane idą przed
    // wszystkimi grupami, żeby operator od razu zgłosił je administratorowi; filtry kategorii
    // zostają w kolejności UCS („Inne" na końcu).
    .sort((a, b) => {
      if (data?.unidentifiedFirst) {
        const d = Number(b.reason !== 'identified') - Number(a.reason !== 'identified');
        if (d) return d;
      }
      return catRank(a) - catRank(b);
    });

  function tryStart(order: UcsPackOrder) {
    if (starting) return;
    if (order.reason === 'identified' && data?.stock?.warnOnStart && order.stock && order.stock.status !== 'ok') {
      AudioFX.buzz();
      setStockAsk(order);
      return;
    }
    startPacking(order);
  }

  async function startPacking(order: UcsPackOrder) {
    if (starting) return;
    // Nierozpoznana metoda dostawy: UCS odmówi wystawienia etykiety (422
    // rate_not_identifiable), więc operator spakowałby paczkę, której nie da się nadać.
    // Kafelek jest też `disabled`, ale bramka zostaje — po odświeżeniu kolejki stan mógł się
    // zmienić między renderem a kliknięciem.
    if (order.reason !== 'identified') {
      AudioFX.buzz();
      push('Nie można spakować — zgłoś to zamówienie administratorowi systemu', 'err');
      return;
    }
    setStarting(order.shopifyOrderId);
    try {
      const r = await axios.post(`${API_URL}/api/packing/start`, {
        shopifyOrderId: order.shopifyOrderId,
        stationId: station?.id,
        operatorId: user?.id,
      });
      localStorage.setItem('currentPackingSession', JSON.stringify({
        sessionId: r.data.sessionId, orderId: r.data.orderId, orderName: order.orderName, timestamp: new Date().toISOString(),
      }));
      setSession({ ...r.data, sourceOrder: order });
      AudioFX.success();
      navigate('/packing-station/session');
    } catch (e: any) {
      AudioFX.buzz();
      push(e?.response?.data?.error?.message || e?.response?.data?.error || 'Błąd rozpoczynania pakowania', 'err');
    } finally {
      setStarting(null);
    }
  }

  return (
    <div className="screen">
      <div className="k-header">
        <button className="back" onClick={() => navigate('/packing-station/dashboard')}><Icon name="arrow-left" size={20} /></button>
        <div><div className="k-title">Zamówienia</div><div className="k-sub">{orders.length} w kolejce UCS · auto-odświeżanie</div></div>
      </div>
      <div style={{ flex: 'none', padding: '12px 20px 0' }}>
        <div className="scan-input" style={{ border: '1px solid var(--border-strong)', padding: '10px 14px' }}>
          <Icon name="search" size={18} style={{ color: 'var(--fg-3)' }} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Szukaj zamówienia / klienta…" style={{ fontFamily: 'var(--font-ui)' }} />
        </div>
      </div>
      <div className="filters">
        <button className="fchip" aria-pressed={filter === 'all'} onClick={() => { AudioFX.click(); setFilter('all'); }}>
          Wszystkie<span className="cnt">{counts.all || 0}</span>
        </button>
        {categories.map((c) => {
          const key = c.id ?? 'others';
          return (
            <button key={key} className="fchip" aria-pressed={filter === key} onClick={() => { AudioFX.click(); setFilter(key); }} title={c.cutoffTime ? `Pakować do ${c.cutoffTime}` : undefined}>
              {c.name}
              {c.cutoffTime && <span className="muted" style={{ fontSize: 11, marginLeft: 4 }}>do {c.cutoffTime}</span>}
              <span className="cnt">{counts[key] || 0}</span>
            </button>
          );
        })}
      </div>

      <div className="scrollable" style={{ flex: 1 }}>
        {isLoading && !orders.length ? (
          <div className="order-grid" style={{ gridTemplateColumns: compact ? '1fr' : '1fr 1fr' }}>
            {[0, 1, 2, 3].map((i) => <div key={i} className="skel" style={{ height: 120 }} />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="state-msg">
            <div className="si"><Icon name="package-open" size={30} /></div>
            <h3>Brak zamówień do spakowania</h3><p>Wszystko spakowane. Dobra robota!</p>
          </div>
        ) : (
          <div className="order-grid" style={{ gridTemplateColumns: compact ? '1fr' : '1fr 1fr' }}>
            {filtered.map((o) => {
              const totalItems = o.items.reduce((s, it) => s + (it.quantity || 0), 0);
                const blocked = o.reason !== 'identified';
                const stockTag = o.stock || null;
                const stockTone = (stockTag && STOCK_TONE[stockTag.status]) || STOCK_TONE.partial;
                return (
                <button
                  className={`order-tile${blocked ? ' tile-blocked' : ''}`}
                  key={o.shopifyOrderId}
                  disabled={!!starting || blocked}
                  aria-disabled={blocked}
                  title={blocked ? 'Nierozpoznana metoda dostawy — zgłoś administratorowi systemu' : undefined}
                  onClick={() => tryStart(o)}
                >
                  <div className="ot-top">
                    <span className="ot-num">{o.orderName}</span>
                    <CourierBadge
                      courier={blocked ? 'unknown' : courierOfTitle(o.carrier?.title || o.carrier?.pluginId || '')}
                    />
                  </div>
                  <div>
                    <div className="ot-cust" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>{o.customer?.name || '—'}</span>
                      {o.priority?.vip && (
                        <span className="tag-chip" style={{ background: 'var(--warning-soft)', color: 'var(--warning-text)' }} title="Klient VIP — priorytet">VIP</span>
                      )}
                      {o.priority?.mode === 'advanced' && (o.priority.score ?? 0) > 0 && !o.priority.vip && (
                        <span className="tag-chip" title="Punkty priorytetu klienta">★ {o.priority.score}</span>
                      )}
                    </div>
                    <div className="ot-meta">
                      <span><Icon name="map-pin" size={13} style={{ verticalAlign: '-2px' }} /> {o.customer?.city || '—'}</span>
                      <span>· {totalItems} pozycji</span>
                      {o.deliveryDate && <span className="tag-chip">{o.deliveryDate}</span>}
                      {/* Bez znacznika „spakowane": skoro zamówienie jest w kolejce, to jest do spakowania
                          (backend /packages/now je zwrócił). „w trakcie" zostaje — sygnalizuje, że ktoś
                          właśnie pakuje, ale NIE blokuje rozpoczęcia (np. po odświeżeniu/przejęciu stacji). */}
                      {o.packingStatus === 'packing' && <span className="tag-chip" style={{ background: 'var(--warning-soft)', color: 'var(--warning-text)' }}>w trakcie</span>}
                      {/* Ktoś już wszedł w zamówienie i wydrukował etykiety produktowe — następna osoba nie
                          drukuje ich drugi raz. Informacyjny, tak jak „w trakcie": NIE blokuje startu. */}
                      {o.labelsPrinted && (
                        <span className="tag-chip" style={{ background: 'var(--info-soft)', color: 'var(--info-text)' }} title="Etykiety produktowe tego zamówienia są już wydrukowane">
                          etykiety wydrukowano
                        </span>
                      )}
                      {stockTag && !blocked && (
                        <span className="tag-chip" style={{ background: stockTone.bg, color: stockTone.fg, display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700 }} title="Stan magazynu dla tego zamówienia">
                          <Icon name={stockTone.icon} size={12} /> {stockTag.label}
                        </span>
                      )}
                      {starting === o.shopifyOrderId && <span className="tag-chip" style={{ background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>start…</span>}
                    </div>
                  </div>
                  {!blocked && stockTag && stockTag.status !== 'ok' && stockTag.missing.length > 0 && (
                    <div
                      style={{ fontSize: 12.5, fontWeight: 600, color: stockTone.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'left', alignSelf: 'stretch', minWidth: 0, maxWidth: '100%' }}
                      title={missingText(stockTag)}
                    >
                      brak: {missingText(stockTag)}
                    </div>
                  )}
                  {blocked && (
                    <div className="ot-note ot-note-block">
                      <Icon name="alert-circle" size={13} />{' '}
                      <b>Nie można spakować.</b>{' '}
                      {o.reason === 'no_shipping_line'
                        ? 'Zamówienie nie ma linii wysyłkowej.'
                        : 'Metoda dostawy nie jest rozpoznana.'}{' '}
                      Zgłoś numer {o.orderName} administratorowi systemu — musi wykonać
                      konwersję metody dostawy.
                    </div>
                  )}
                </button>
                );
            })}
          </div>
        )}
      </div>
      {stockAsk && stockAsk.stock && (
        <ConfirmDialog
          title={`${stockAsk.orderName}: ${stockAsk.stock.status === 'short' ? 'brak na stanie' : 'niepełny stan'}`}
          desc={`Brakuje: ${missingText(stockAsk.stock)}. Pakować mimo to?`}
          confirmLabel="Pakuj mimo to"
          onCancel={() => setStockAsk(null)}
          onConfirm={() => { const o = stockAsk; setStockAsk(null); startPacking(o); }}
        />
      )}
    </div>
  );
}
